import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import ServiceAgreementGate from "../components/ServiceAgreementGate";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Ticket, Clock, CheckCircle2, AlertCircle, ExternalLink, Package, Mail, FileText, AlertTriangle, CreditCard, ShieldCheck, DollarSign, Building, Copy, Zap } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Progress } from "../components/ui/progress";
import { Separator } from "../components/ui/separator";

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

const statusColors = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  pending_client: "bg-purple-100 text-purple-700",
  resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-700",
};

const statusLabels = {
  open: "Open",
  in_progress: "In Progress",
  pending_client: "Awaiting Response",
  resolved: "Resolved",
  closed: "Closed",
};

const paymentStatusColors = {
  pending_payment: "bg-red-100 text-red-700 border-red-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  covered_by_sla: "bg-blue-100 text-blue-700 border-blue-200",
  covered_by_product: "bg-teal-100 text-teal-700 border-teal-200",
};

const paymentStatusLabels = {
  pending_payment: "Payment Required",
  paid: "Paid",
  covered_by_sla: "SLA Covered",
  covered_by_product: "Product Support",
};

const sourceIcons = {
  portal: Ticket,
  email: Mail,
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState({});
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slaOverview, setSlaOverview] = useState(null);
  const [expandedContract, setExpandedContract] = useState(null);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: "",
    description: "",
    category: "general",
    priority: "medium",
    product_id: "",
    service_type: "",
  });
  const [serviceTypes, setServiceTypes] = useState(null);
  const [hasAgreement, setHasAgreement] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, productsRes] = await Promise.all([
        apiClient.get("/stats/dashboard"),
        apiClient.get("/tickets"),
        apiClient.get("/products?active_only=true"),
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data);
      setProducts(productsRes.data);
      // Fetch SLA overview
      try {
        const slaRes = await apiClient.get("/client/sla-overview");
        setSlaOverview(slaRes.data);
      } catch {}
      // Fetch invoices
      try {
        const invRes = await apiClient.get("/invoices");
        setClientInvoices(invRes.data);
      } catch {}
      // Check service agreement
      try {
        const agreeRes = await apiClient.get(`/agreements/check/${user?.id}`);
        setHasAgreement(agreeRes.data.has_agreement);
      } catch {
        setHasAgreement(false);
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      const ticketData = { ...newTicket };
      if (!ticketData.product_id) delete ticketData.product_id;
      if (!ticketData.service_type) delete ticketData.service_type;
      
      await apiClient.post("/tickets", ticketData);
      toast.success("Ticket created successfully! Check your email for confirmation.");
      setCreateDialogOpen(false);
      setNewTicket({ title: "", description: "", category: "general", priority: "medium", product_id: "", service_type: "" });
      setServiceTypes(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to create ticket");
    }
  };

  const isProductClient = user?.client_type === "product_client";

  const handleProductChange = async (productId) => {
    const product = products.find(p => p.id === productId);
    setNewTicket({ ...newTicket, product_id: productId === "none" ? "" : productId, service_type: "" });
    
    if (isProductClient && product) {
      try {
        const res = await apiClient.get(`/product-service-types/${encodeURIComponent(product.name)}`);
        setServiceTypes(res.data);
      } catch {
        setServiceTypes(null);
      }
    } else {
      setServiceTypes(null);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="sag-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-slate-500 font-medium">{label}</p>
            <p className="text-2xl font-bold font-heading text-slate-900">{value || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">{t("common.welcome")}, {user?.full_name}</h1>
            <p className="text-slate-500 mt-1">{t("dashboard.manageTickets")}</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sag-navy hover:bg-sag-navy-800" data-testid="create-ticket-button">
                <Plus className="h-4 w-4 mr-2" />
                {t("tickets.newTicket")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create Support Ticket</DialogTitle>
                <DialogDescription>
                  Describe your issue and we'll connect you with the right consultant.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTicket} className="space-y-4 mt-4">
                {products.length > 0 && (
                  <div className="space-y-2">
                    <Label>Product</Label>
                    <Select
                      value={newTicket.product_id || "none"}
                      onValueChange={handleProductChange}
                    >
                      <SelectTrigger data-testid="ticket-product-select">
                        <SelectValue placeholder="Select a product..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">General Inquiry</SelectItem>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Service Type — shown for product clients when a product is selected */}
                {isProductClient && serviceTypes && newTicket.product_id && (
                  <div className="space-y-2" data-testid="service-type-section">
                    <Label>Service Type</Label>
                    <Select
                      value={newTicket.service_type || ""}
                      onValueChange={(value) => setNewTicket({ ...newTicket, service_type: value })}
                    >
                      <SelectTrigger data-testid="ticket-service-type-select">
                        <SelectValue placeholder="Select service type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.covered?.map((st) => (
                          <SelectItem key={st} value={st}>
                            {st} — Covered
                          </SelectItem>
                        ))}
                        {serviceTypes.billable?.map((st) => (
                          <SelectItem key={st} value={st}>
                            {st} — Payment Required
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newTicket.service_type && serviceTypes.billable?.includes(newTicket.service_type) && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        <p className="text-xs text-amber-700">
                          <strong>{newTicket.service_type}</strong> is not included in your subscription. Payment will be required before work can begin.
                        </p>
                      </div>
                    )}
                    {newTicket.service_type && serviceTypes.covered?.includes(newTicket.service_type) && (
                      <div className="p-2 bg-teal-50 border border-teal-200 rounded-md flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-teal-600 flex-shrink-0" />
                        <p className="text-xs text-teal-700">
                          <strong>{newTicket.service_type}</strong> is included in your subscription. No additional charges.
                        </p>
                      </div>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Brief description of your issue"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                    required
                    data-testid="ticket-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide details about your request..."
                    rows={4}
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    required
                    data-testid="ticket-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={newTicket.category}
                      onValueChange={(value) => setNewTicket({ ...newTicket, category: value })}
                    >
                      <SelectTrigger data-testid="ticket-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="technical">Technical Support</SelectItem>
                        <SelectItem value="integration">Integration Help</SelectItem>
                        <SelectItem value="consulting">Consulting Request</SelectItem>
                        <SelectItem value="billing">Billing Question</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={newTicket.priority}
                      onValueChange={(value) => setNewTicket({ ...newTicket, priority: value })}
                    >
                      <SelectTrigger data-testid="ticket-priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-sag-navy hover:bg-sag-navy-800" data-testid="submit-ticket-button">
                    Create Ticket
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard icon={Ticket} label={t("dashboard.totalTickets")} value={stats.my_tickets} color="bg-sag-navy/10 text-sag-navy" />
          <StatCard icon={AlertCircle} label={t("dashboard.openTickets")} value={stats.open_tickets} color="bg-amber-100 text-amber-600" />
          <StatCard icon={CheckCircle2} label={t("dashboard.resolved")} value={stats.resolved_tickets} color="bg-emerald-100 text-emerald-600" />
        </div>

        {/* SLA Contract Overview (for SLA clients) */}
        {slaOverview && slaOverview.contracts && slaOverview.contracts.length > 0 && (
          <div className="space-y-4" data-testid="sla-overview-section">
            <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-sag-navy" />
              {t("sla.title")}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {slaOverview.contracts.map((contract) => {
                const isExpanded = expandedContract === contract.contract_id;
                const progressColor = contract.percent_consumed >= 100
                  ? "bg-red-500"
                  : contract.percent_consumed >= 80
                  ? "bg-amber-500"
                  : "bg-emerald-500";
                return (
                  <Card key={contract.contract_id} className="sag-card" data-testid={`sla-contract-${contract.contract_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-heading font-semibold text-slate-900">{contract.title}</h3>
                          {contract.product_name && (
                            <p className="text-xs text-slate-500 mt-0.5">{contract.product_name}</p>
                          )}
                        </div>
                        <Badge className={contract.exceeded ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}>
                          {contract.exceeded ? t("sla.exceeded") || "Exceeded" : t("common.active")}
                        </Badge>
                      </div>
                      {/* Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">{t("sla.usedHours")}: <strong className="text-slate-900">{contract.hours_used}h</strong></span>
                          <span className="text-slate-500">{t("sla.totalHours")}: <strong className="text-slate-900">{contract.total_hours}h</strong></span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${progressColor}`}
                            style={{ width: `${Math.min(contract.percent_consumed, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-slate-400">
                          <span>{contract.percent_consumed}% {t("sla.consumed") || "consumed"}</span>
                          <span>{t("sla.remainingHours")}: {contract.hours_remaining}h</span>
                        </div>
                      </div>
                      {/* Overage Warning */}
                      {contract.exceeded && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="sla-overage-warning">
                          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-red-700">
                            {t("sla.overageWarning") || `You have exceeded your allocated SLA hours by ${contract.overage_hours}h. Additional work will be billed accordingly.`}
                          </p>
                        </div>
                      )}
                      {contract.percent_consumed >= 80 && !contract.exceeded && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-amber-700">
                            {t("sla.nearLimitWarning") || `You have used ${contract.percent_consumed}% of your allocated hours. Please plan accordingly.`}
                          </p>
                        </div>
                      )}
                      {/* Billing Info */}
                      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-500 border-t pt-3">
                        {contract.monthly_fee && (
                          <span>Monthly Fee: <strong className="text-slate-900">${contract.monthly_fee}/mo</strong></span>
                        )}
                        <span>{t("sla.billingPeriod")}: <strong className="text-slate-900 capitalize">{contract.billing_cycle}</strong></span>
                        {contract.hourly_rate && <span>Hourly Rate: <strong className="text-slate-900">${contract.hourly_rate}/h</strong></span>}
                        {contract.overage_rate && <span>Overage Rate: <strong className="text-red-600">${contract.overage_rate}/h</strong></span>}
                        {contract.response_time_standard && (
                          <span>Response Time: <strong className="text-slate-900">{contract.response_time_standard}</strong></span>
                        )}
                        {contract.response_time_emergency && (
                          <span>Emergency: <strong className="text-amber-700">{contract.response_time_emergency}</strong></span>
                        )}
                        {contract.next_billing_date && (
                          <span>Next Billing: <strong className="text-slate-900">{new Date(contract.next_billing_date).toLocaleDateString()}</strong></span>
                        )}
                        <span className="capitalize">
                          Payment: <strong className="text-slate-900">{(contract.payment_processor || "fiserv") === "fiserv" ? "Scotiabank" : contract.payment_processor}</strong>
                        </span>
                      </div>
                      {/* Expand Time Entries */}
                      {contract.time_entries.length > 0 && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => setExpandedContract(isExpanded ? null : contract.contract_id)}
                            className="text-xs text-sag-blue hover:text-sag-navy font-medium flex items-center gap-1"
                            data-testid={`expand-entries-${contract.contract_id}`}
                          >
                            <Clock className="h-3 w-3" />
                            {isExpanded ? (t("sla.hideEntries") || "Hide time entries") : (t("sla.viewEntries") || `View ${contract.time_entries.length} time entries`)}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto" data-testid="time-entries-list">
                              {contract.time_entries.map((entry) => (
                                <div key={entry.id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-700 truncate">{entry.ticket_title || entry.description || "Time entry"}</p>
                                    <p className="text-slate-400">{entry.consultant_name} &middot; {new Date(entry.created_at).toLocaleDateString()}</p>
                                  </div>
                                  <Badge variant="secondary" className="ml-2 flex-shrink-0">{entry.hours}h</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Ad Hoc Client — Payment Policy Banner */}
        {(!slaOverview || (slaOverview.client_type === "ad_hoc" && !slaOverview.contracts?.length)) && (
          <Card className="border-2 border-red-300 bg-gradient-to-r from-red-50 to-amber-50" data-testid="adhoc-payment-policy">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-xl flex-shrink-0">
                  <AlertTriangle className="h-6 w-6 text-red-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-red-900 text-lg">No Work Will Commence Without Payment</h3>
                  <p className="text-sm text-slate-700 mt-1">
                    As a non-SLA client, all service requests require confirmed payment before any work, diagnostics, or troubleshooting begins.
                    Submitting a ticket does not authorize service delivery — a consultant will only be assigned once payment is confirmed.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Need ongoing coverage? Contact us about our SLA packages for priority service and prepaid hours.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-sag-navy hover:bg-sag-navy-800"
                      onClick={() => navigate("/services")}
                      data-testid="pay-for-service-btn"
                    >
                      <CreditCard className="h-4 w-4 mr-1.5" /> Pay for Service
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-sag-navy text-sag-navy"
                      onClick={() => navigate("/client/invoices")}
                      data-testid="view-invoices-btn"
                    >
                      <FileText className="h-4 w-4 mr-1.5" /> View Invoices
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Product Client — Support Included Badge */}
        {slaOverview?.client_type === "product_client" && (
          <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200" data-testid="product-client-badge">
            <CardContent className="p-4 flex items-center gap-3">
              <Package className="h-5 w-5 text-teal-600" />
              <p className="text-sm font-medium text-teal-800">
                Product Support Active — Tech support is included with your product purchase. Submit tickets anytime.
              </p>
            </CardContent>
          </Card>
        )}

        {/* SLA Client — Active Coverage Badge */}
        {slaOverview?.client_type === "sla" && slaOverview.contracts?.length > 0 && (
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="sla-active-badge">
            <CardContent className="p-4 flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-800">
                SLA Active — Your service requests are covered under your agreement. No prepayment required.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Service Agreement Acknowledgement — shown if SLA/Product client hasn't signed yet */}
        {hasAgreement === false && (slaOverview?.client_type === "sla" || slaOverview?.client_type === "product_client") && (
          <Card className="border-2 border-amber-400 bg-amber-50" data-testid="agreement-required">
            <CardContent className="p-0">
              <ServiceAgreementGate
                onAgreementSigned={() => {
                  setHasAgreement(true);
                  toast.success("Service agreement acknowledged. Welcome!");
                }}
                onCancel={() => {}}
              />
            </CardContent>
          </Card>
        )}

        {/* Bank Deposit / Direct Deposit Details */}
        {(slaOverview?.client_type === "sla" || slaOverview?.client_type === "product_client") && (
          <Card className="sag-card" data-testid="bank-deposit-section">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                  <Building className="h-5 w-5 text-blue-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-slate-900 text-base">Payment via Direct Deposit</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Send your payment directly to our Scotiabank account</p>
                  
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Bank</span>
                      <span className="text-sm font-semibold text-slate-900">ScotiaBank Bahamas</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Account Name</span>
                      <span className="text-sm font-semibold text-slate-900">Sebastian Alliance Group Ltd.</span>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Account Number</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-bold text-slate-900">171287</span>
                        <button
                          type="button"
                          className="p-1 hover:bg-slate-200 rounded"
                          onClick={() => { navigator.clipboard.writeText("171287"); toast.success("Account number copied"); }}
                          data-testid="copy-account-number"
                        >
                          <Copy className="h-3.5 w-3.5 text-slate-400" />
                        </button>
                      </div>
                    </div>
                    <Separator />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">Branch Number</span>
                      <span className="text-sm font-mono font-bold text-slate-900">03465 (Carmichael)</span>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-amber-800 font-semibold">After depositing, you must confirm your payment</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Direct deposits are processed manually. After making your deposit, please submit a payment confirmation 
                          through the helpdesk so we can verify and activate your services promptly.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Outstanding Invoices */}
        {clientInvoices.filter((i) => i.status === "pending" || i.status === "overdue").length > 0 && (
          <Card className="border-amber-200 bg-amber-50" data-testid="client-outstanding-invoices">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <h3 className="font-heading font-bold text-slate-900">Outstanding Invoices</h3>
              </div>
              <div className="space-y-2">
                {clientInvoices.filter((i) => i.status === "pending" || i.status === "overdue").map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-amber-200">
                    <div>
                      <p className="text-sm font-medium">{inv.invoice_number}: {inv.items?.[0]?.description || inv.billing_type}</p>
                      <p className="text-xs text-slate-500">Due: {new Date(inv.due_date).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sag-navy">${inv.total.toLocaleString()} {inv.currency}</p>
                      <Badge className={inv.status === "overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>
                        <span className="capitalize">{inv.status}</span>
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => navigate("/client/invoices")} data-testid="view-all-invoices-btn">
                View All Invoices
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Banner */}
        <Card className="bg-gradient-to-r from-sag-navy to-sag-navy-800 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/10 rounded-xl">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">{t("dashboard.emailSupport")}</h3>
                <p className="text-white/80 text-sm mt-1">
                  {t("dashboard.emailSupportDesc")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        <Card className="sag-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-xl">{t("tickets.yourTickets")}</CardTitle>
            <CardDescription>{t("tickets.viewManage")}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="p-8 text-center">
                <Ticket className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No tickets yet. Create one to get started!</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {tickets.map((ticket) => {
                  const SourceIcon = sourceIcons[ticket.source] || Ticket;
                  return (
                    <div
                      key={ticket.id}
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      data-testid={`ticket-item-${ticket.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <SourceIcon className="h-4 w-4 text-slate-400" />
                            <h3 className="font-semibold text-slate-900 truncate">{ticket.title}</h3>
                          </div>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{ticket.description}</p>
                          <div className="flex items-center gap-3 mt-3 flex-wrap">
                            <Badge className={statusColors[ticket.status]} variant="secondary">
                              {statusLabels[ticket.status]}
                            </Badge>
                            <Badge className={priorityColors[ticket.priority]} variant="secondary">
                              {ticket.priority}
                            </Badge>
                            {ticket.payment_status && (
                              <Badge className={paymentStatusColors[ticket.payment_status] || "bg-slate-100 text-slate-600"} variant="secondary" data-testid={`payment-badge-${ticket.id}`}>
                                {ticket.payment_status === "pending_payment" && <DollarSign className="h-3 w-3 mr-1" />}
                                {ticket.payment_status === "paid" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                                {ticket.payment_status === "covered_by_sla" && <ShieldCheck className="h-3 w-3 mr-1" />}
                                {ticket.payment_status === "covered_by_product" && <Package className="h-3 w-3 mr-1" />}
                                {paymentStatusLabels[ticket.payment_status] || ticket.payment_status}
                              </Badge>
                            )}
                            {ticket.product_name && (
                              <Badge variant="outline" className="border-sag-blue text-sag-blue">
                                <Package className="h-3 w-3 mr-1" />
                                {ticket.product_name}
                              </Badge>
                            )}
                            <span className="text-xs text-slate-400">
                              <Clock className="h-3 w-3 inline mr-1" />
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <ExternalLink className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
